import React, { useState, useEffect, useCallback } from 'react';
import { HiOutlineRefresh, HiOutlineTrash, HiOutlineChevronLeft } from 'react-icons/hi';
import axiosInstance from '../../../api/axiosConfig';
import BatchReview from './BatchReview';

const STATUS_CLASS = {
    parsed: 'pd-pill-muted',
    sending: 'pd-pill-warn',
    completed: 'pd-pill-ok',
    failed: 'pd-pill-err'
};

const BatchesTab = ({ canSend, canDelete, gatewayReady }) => {
    const [batches, setBatches] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchBatches = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/payslips/batches', { params: { limit: 50 } });
            setBatches(res.data?.data || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load history');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchBatches(); }, [fetchBatches]);

    const openBatch = async (id) => {
        try {
            const res = await axiosInstance.get(`/api/payslips/batches/${id}`);
            setSelected(res.data?.data || null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to open the batch');
        }
    };

    const refreshSelected = useCallback(async () => {
        if (selected?._id) await openBatch(selected._id);
        fetchBatches();
    }, [selected, fetchBatches]);

    const removeBatch = async (batch) => {
        if (!window.confirm(`Delete the ${batch.payrollMonthLabel || batch.payrollMonth} batch and its split PDFs?\n\nThe delivery log is deleted with it.`)) return;
        try {
            await axiosInstance.delete(`/api/payslips/batches/${batch._id}`);
            if (selected?._id === batch._id) setSelected(null);
            fetchBatches();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete');
        }
    };

    if (selected) {
        return (
            <div className="pd-stack">
                <button className="pd-btn pd-btn-ghost pd-self-start" onClick={() => setSelected(null)}>
                    <HiOutlineChevronLeft size={16} /> Back to history
                </button>
                <BatchReview
                    batch={selected}
                    onRefresh={refreshSelected}
                    canSend={canSend}
                    gatewayReady={gatewayReady}
                />
            </div>
        );
    }

    return (
        <div className="pd-card">
            <div className="pd-card-head">
                <div>
                    <h3>Distribution history</h3>
                    <p className="pd-muted">Every payroll run that has been split, with its delivery outcome.</p>
                </div>
                <button className="pd-btn pd-btn-ghost" onClick={fetchBatches} disabled={loading}>
                    <HiOutlineRefresh size={16} /> Refresh
                </button>
            </div>

            {error && <div className="pd-banner pd-banner-err">{error}</div>}

            {loading ? (
                <p className="pd-muted">Loading…</p>
            ) : batches.length === 0 ? (
                <p className="pd-muted">No payroll runs yet. Upload a PDF from the “Upload &amp; Send” tab.</p>
            ) : (
                <div className="pd-table-wrap">
                    <table className="pd-table">
                        <thead>
                            <tr>
                                <th>Period</th>
                                <th>File</th>
                                <th>Slips</th>
                                <th>Sent</th>
                                <th>Failed</th>
                                <th>Not sendable</th>
                                <th>Status</th>
                                <th>Uploaded</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {batches.map((batch) => (
                                <tr key={batch._id} className="pd-row-click" onClick={() => openBatch(batch._id)}>
                                    <td><strong>{batch.payrollMonthLabel || batch.payrollMonth}</strong></td>
                                    <td className="pd-truncate">{batch.originalFilename}</td>
                                    <td className="pd-num">{batch.totals?.payslips || 0}</td>
                                    <td className="pd-num">{batch.totals?.sent || 0}</td>
                                    <td className="pd-num">{batch.totals?.failed || 0}</td>
                                    <td className="pd-num">{batch.totals?.unmatched || 0}</td>
                                    <td>
                                        <span className={`pd-pill ${STATUS_CLASS[batch.status] || 'pd-pill-muted'}`}>
                                            {batch.status}
                                        </span>
                                    </td>
                                    <td className="pd-muted">
                                        {new Date(batch.createdAt).toLocaleString('en-GB', { timeZone: 'Asia/Qatar' })}
                                    </td>
                                    <td>
                                        {canDelete && (
                                            <button
                                                className="pd-icon-btn pd-icon-danger"
                                                title="Delete batch"
                                                onClick={(e) => { e.stopPropagation(); removeBatch(batch); }}
                                            >
                                                <HiOutlineTrash size={16} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default BatchesTab;
